import { Badge, cn } from '@codezo/quote-detail';
import { MessageSquare, Truck } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { SampleRound, SampleStep } from '../types';
import { StepDot } from '../ui/StepDot';

/**
 * A product's sampling, as the design draws it: a spine inside a spine.
 *
 * ── Why the sub-steps and not a box ──
 *
 * "Sample & approval" is one row on the order flow and six states underneath it.
 * Naming the current one in a strip — "Round 1 · In production", a button beside
 * it — answers *what can I do* and nothing else: not what came before it, not
 * what is left, not whether this is the second attempt. The design gives the
 * step the product is standing on the same grammar as every other step on the
 * page — dot, label, right slot — one level in and one size down, so the row
 * opens into the same thing it is part of.
 *
 * The move goes bare on its step, not in an ActionBox. A box exists to pair a
 * button with the thing it acts on, and here the row IS that thing: the strip
 * would only restate the label directly above it. ActionBox is still what the
 * later rows use, and what `footer` renders into when the turn is the other
 * party's — "waiting on the client's review" is a statement, not a step.
 *
 * ── Rounds are history, not a redraw ──
 *
 * A declined round keeps its media, its tracking and the client's reason
 * forever: they are what the next round is judged against. Past rounds open
 * read-only — no stepper, because how far a replaced round got is not the
 * question anyone is asking of it.
 *
 * Presentational, like everything else here. States, labels and dates are the
 * backend's own lifecycle, already mapped and formatted by the caller; every
 * control is a slot.
 */
export function SampleRounds({ rounds }: { rounds: SampleRound[] }) {
	const live = rounds.length > 0 ? rounds[rounds.length - 1] : null;
	const liveId = live?.id ?? null;
	const [viewing, setViewing] = useState(liveId);

	// A new round starts: follow it. Looking at round 2's history while round 3
	// is the one with a move on it is a way to miss the move.
	useEffect(() => {
		if (liveId) setViewing(liveId);
	}, [liveId]);

	if (!live) return null;
	const round = rounds.find((r) => r.id === viewing) ?? live;
	const past = round.id !== live.id;
	/**
	 * A DECLINED round is over, replacement or not.
	 *
	 * This used to key the record block on `past` alone — on a round being
	 * superseded — so between the client declining and the studio greenlighting
	 * round 2 the newest round was still `live` and drew a stepper: a run of dots
	 * for a round that had ended, with the client's reason sitting in the payload
	 * unrendered. The decline was invisible at exactly the moment it was the only
	 * thing anyone needed to read.
	 *
	 * An APPROVED round keeps its stepper: its last step IS the approval, so the
	 * run is the record.
	 */
	const over = past || round.state === 'declined';

	return (
		<div className="mt-2.5">
			{rounds.length > 1 && (
				<div className="mb-3 flex flex-wrap items-center gap-1.5">
					{rounds.map((r) => {
						const on = r.id === round.id;
						return (
							<button
								key={r.id}
								type="button"
								onClick={() => setViewing(r.id)}
								className={cn(
									'inline-flex items-center gap-1.5 rounded-[var(--r-2)] border px-2.5 py-1 text-xs',
									on
										? 'border-accent bg-accent-soft text-text-1'
										: 'border-line-2 bg-surface text-text-3'
								)}
							>
								Round {r.round}
								<span
									className={cn(
										'font-mono text-2xs',
										r.state === 'declined' ? 'text-status-warn' : 'text-text-4'
									)}
								>
									{r.state === 'declined'
										? 'declined'
										: r.state === 'approved'
											? 'approved'
											: 'current'}
								</span>
							</button>
						);
					})}
				</div>
			)}

			{over ? (
				<div>
					<div className="flex flex-wrap items-center gap-2 rounded-[var(--r-2)] border border-line-2 bg-surface px-3 py-2">
						{round.state === 'declined' ? (
							<Badge tone="warn" dot>
								Declined
							</Badge>
						) : (
							<Badge tone="success" dot>
								Approved
							</Badge>
						)}
						<span className="text-xs text-text-3">
							Round {round.round}
							{round.declinedAt
								? ` · declined by the client ${round.declinedAt}`
								: ''}
						</span>
						{/* Only on a SUPERSEDED round. The newest declined round is
						    still the one the studio acts on — "read-only" beside the
						    button that starts its replacement would be a lie. */}
						{past && <span className="ml-auto text-2xs text-text-4">read-only</span>}
					</div>
					{round.declineReason && (
						<div className="mx-0.5 mt-2.5 flex gap-2">
							<MessageSquare
								size={12}
								className="mt-px shrink-0 text-text-4"
								aria-hidden
							/>
							<span className="text-xs leading-relaxed text-text-3">
								<span className="text-text-4">Client&rsquo;s reason: </span>
								{round.declineReason}
							</span>
						</div>
					)}
					{/* What the client SENT with the decline, under what they said
					    about it. Kept apart from `media` below, which is the studio's
					    own photographs of the round — the two are the objection and
					    the thing objected to, and one strip for both makes the
					    client's evidence read as the studio's work. */}
					{round.declineMedia}
				</div>
			) : (
				<div>
					{round.steps.map((step, i) => (
						<SampleStepRow
							key={step.key}
							step={step}
							last={i === round.steps.length - 1}
						/>
					))}
				</div>
			)}

			{round.media}
			{round.note && (
				<div className="mx-0.5 mt-2.5 flex gap-2">
					<MessageSquare size={12} className="mt-px shrink-0 text-text-4" aria-hidden />
					<span className="text-xs leading-relaxed text-text-3">{round.note}</span>
				</div>
			)}
			{round.tracking && (
				<div className="mt-2.5 ml-[22px] flex flex-wrap items-center gap-2">
					<Truck size={13} className="shrink-0 text-text-3" aria-hidden />
					<span className="text-xs text-text-4">{round.tracking.label}</span>
					{round.tracking.action}
				</div>
			)}
			{!past && round.footer}
		</div>
	);
}

/**
 * A run of sub-steps, one level in from the spine.
 *
 * Exported because the product side needs the same thing: payment, production,
 * shipping and arrival are four steps of ONE passage, and drawn as four
 * top-level rows they read as four unrelated stages the way sampling did before
 * it was nested. A parent row and its steps is the grammar this page settled on;
 * there should be one of it, not one per subject.
 */
export function FlowSubSteps({ steps }: { steps: SampleStep[] }) {
	if (steps.length === 0) return null;
	return (
		<div className="mt-2.5">
			{steps.map((step, i) => (
				<SampleStepRow key={step.key} step={step} last={i === steps.length - 1} />
			))}
		</div>
	);
}

/**
 * One sub-step: the same dot-label-slot row the flow uses, at the inner size.
 *
 * Not `FlowRowView` reused — that row carries a panel, a meta slot and a 14px
 * rail sized to sit against a section head. Nesting it inside itself would read
 * as another top-level step rather than a part of one.
 */
function SampleStepRow({ step, last }: { step: SampleStep; last: boolean }) {
	const done = step.state === 'done';
	const active = step.state === 'active';
	const muted = !done && !active;

	return (
		<div className="flex gap-2.5">
			{/* 12px, EVEN — see `StepDot`. At 13 the 4px dot centred on a half pixel. */}
			<div className="flex w-[12px] shrink-0 flex-col items-center">
				<StepDot size={12} state={step.state} />
				{!last && (
					<span
						className="my-[3px] w-px flex-1"
						style={{
							minHeight: 6,
							background: done ? 'var(--accent)' : 'var(--line-2)',
							opacity: done ? 0.4 : 1,
						}}
					/>
				)}
			</div>

			<div className={cn('min-w-0 flex-1', last ? 'pb-0' : 'pb-2')}>
				<div className="-mt-px flex min-h-[13px] flex-wrap items-center gap-2">
					<span
						className={cn(
							'text-xs leading-[13px]',
							active ? 'font-medium text-text-1' : null,
							done ? 'text-text-1' : null,
							muted ? 'text-text-4' : null
						)}
					>
						{step.label}
					</span>
					{/* ONE right-hand cluster, not two `ml-auto` siblings.
					    Two of them split the free space between themselves, so a step
					    carrying both a date and a control had the date floating in the
					    middle of the row while the control sat at the edge — down a
					    six-row stepper that reads as three different right margins.
					    Same grammar the outer `FlowRowView` uses: meta, then control,
					    one cluster, one edge. */}
					{/* `-my-1.5` so a CONTROL does not set the row's height.
					    A step's rail is a 13px line box; an xs button is 24px, so the
					    one row carrying a move was half again taller than the five
					    around it and the dots lost their rhythm exactly where the eye
					    is drawn. Pulling 6px off each side brings the button's
					    contribution back to 12px, under the 13px the label already
					    sets, so every row is the same height and the control overhangs
					    into the padding instead — 5.5px each way against 8px of it.
					    The alternative, tightening the gaps, makes the list denser
					    without making it even: the button row would still be the tall
					    one. */}
					{(step.at || step.action) && (
						<span className="ml-auto -my-1.5 inline-flex items-center gap-3">
							{step.at && (
								<span className="font-mono text-2xs text-text-4">{step.at}</span>
							)}
							{step.action}
						</span>
					)}
				</div>
				{step.note && <div className="mt-1 text-2xs text-text-4">{step.note}</div>}
				{/* `pb-1` so the panel does not butt against the next step's label —
				    the run's own `pb-2` is spacing between two one-line rows and is
				    too tight once a row carries a box. */}
				{step.panel && <div className="pb-1">{step.panel}</div>}
			</div>
		</div>
	);
}
